import { useCallback, useEffect, useMemo, useState } from 'react'
import { supabase } from '../../lib/supabase'
import { getSetting } from '../../lib/settings'
import { getStages } from '../../lib/stages'
import { containsFilter } from '../../lib/outreach/customFields'
import { CONTACTS_CHANGED } from '../shared/events'

/**
 * Mirrors `loadContacts()` at crm/index.html:~2150 — same 11-filter
 * surface, same two-stage query (user_contacts inclusion → contacts
 * select), same pagination size and ordering. Subscribes to
 * `contacts:changed` so legacy mutations (Add modal, drawer save,
 * Nooks import) trigger a re-fetch.
 */

export const CONTACTS_PAGE_SIZE = 50

export interface ContactsFilters {
  search: string
  stage: string
  country: string
  location: string
  phonePrefix: string
  scoreMin: string
  scoreMax: string
  tagFilter: string
  lastContacted: string
  phoneStatus: string
  emailStatus: string
  /** Custom field filter: definition key, its type, and the value to match. */
  customKey: string
  customType: string
  customValue: string
}

export const EMPTY_FILTERS: ContactsFilters = {
  search: '',
  stage: '',
  country: '',
  location: '',
  phonePrefix: '',
  scoreMin: '',
  scoreMax: '',
  tagFilter: '',
  lastContacted: '',
  phoneStatus: '',
  emailStatus: '',
  customKey: '',
  customType: '',
  customValue: '',
}

export interface ContactRow {
  id: string
  full_name: string | null
  first_name: string | null
  last_name: string | null
  company_name: string | null
  company_domain: string | null
  job_title: string | null
  primary_email: string | null
  primary_phone: string | null
  linkedin_url: string | null
  city: string | null
  state: string | null
  country: string | null
  industry: string | null
}

export interface UserContactOverlay {
  contact_id: string
  lifecycle_stage: string | null
  priority_score: number | null
  last_contacted: string | null
  call_notes: string | null
  tags: string[] | null
  phone_status: string | null
  email_status: string | null
}

export interface UseContactsArgs {
  filters: ContactsFilters
  page: number
}

export interface UseContactsResult {
  rows: ContactRow[]
  ucMap: Record<string, UserContactOverlay>
  total: number
  status: 'idle' | 'loading' | 'success' | 'error'
  error: string | null
  rotDays: number
  rotCutoff: string
  refetch: () => void
}

const SPECIAL_STAGES = new Set(['__nodomain', '__noemail', '__stale'])

export function useContacts({ filters, page }: UseContactsArgs): UseContactsResult {
  const [rows, setRows] = useState<ContactRow[]>([])
  const [ucMap, setUcMap] = useState<Record<string, UserContactOverlay>>({})
  const [total, setTotal] = useState(0)
  const [status, setStatus] = useState<UseContactsResult['status']>('idle')
  const [error, setError] = useState<string | null>(null)
  const [refreshKey, setRefreshKey] = useState(0)

  const refetch = useCallback(() => setRefreshKey((k) => k + 1), [])

  // Stale-cutoff calculation. Memoize on rotDays — without this,
  // `new Date().toISOString()` produces a fresh string every render,
  // which made rotCutoff a different value each pass through the
  // dependency array on the fetch effect below. The effect re-ran on
  // every render → setStatus('loading') → re-render → new rotCutoff →
  // infinite loop firing the contacts + user_contacts queries forever.
  const rotDays = Number(getSetting('rot_days', '30')) || 30
  const rotCutoff = useMemo(
    () => new Date(Date.now() - rotDays * 86400000).toISOString(),
    [rotDays],
  )

  // Bus listener for legacy → island sync (Add modal save, drawer save,
  // Nooks import all dispatch this event after their writes).
  useEffect(() => {
    const bus = window.ShaunIslands?.events
    if (!bus) return
    const handler = () => refetch()
    bus.addEventListener(CONTACTS_CHANGED, handler)
    return () => bus.removeEventListener(CONTACTS_CHANGED, handler)
  }, [refetch])

  useEffect(() => {
    let cancelled = false
    setStatus('loading')
    setError(null)

    void (async () => {
      try {
        // Prefer SessionContext via useSession() — works even when the
        // legacy host hasn't yet syncd window.user. Falls back to
        // window.user for callers that don't pass user via mount props.
        const userId =
          (typeof window !== 'undefined' &&
            (window as unknown as { user?: { id?: string } }).user?.id) ||
          (await (async () => {
            try {
              const { data } = await supabase.auth.getSession()
              return data.session?.user?.id ?? null
            } catch {
              return null
            }
          })())
        if (!userId) {
          setStatus('error')
          setError('Not signed in')
          return
        }

        const f = filters
        const search = f.search.trim()
        const fromIdx = (page - 1) * CONTACTS_PAGE_SIZE
        const toIdx = fromIdx + CONTACTS_PAGE_SIZE - 1

        // ── Stage 1: user_contacts inclusion filter ──────────────────
        const customContains = f.customKey
          ? containsFilter({ key: f.customKey, type: f.customType as never }, f.customValue)
          : null
        const needsUcInclusion =
          customContains !== null ||
          (f.stage && !SPECIAL_STAGES.has(f.stage)) ||
          f.scoreMin !== '' ||
          f.scoreMax !== '' ||
          f.tagFilter !== '' ||
          f.phoneStatus !== '' ||
          f.emailStatus !== ''

        let ucIncludeIds: string[] | null = null
        if (needsUcInclusion) {
          let ucQ = supabase.from('user_contacts').select('contact_id').eq('user_id', userId)

          if (f.stage === '__stale') {
            const closedNames = getStages().filter((s) =>
              ['closed', 'do not contact'].includes(s.toLowerCase()),
            )
            ucQ = ucQ.or(`last_contacted.is.null,last_contacted.lt.${rotCutoff}`)
            if (closedNames.length) {
              ucQ = ucQ.not(
                'lifecycle_stage',
                'in',
                `(${closedNames.map((s) => `"${s}"`).join(',')})`,
              )
            }
          } else if (f.stage) {
            ucQ = ucQ.eq('lifecycle_stage', f.stage)
          }
          if (f.scoreMin !== '') ucQ = ucQ.gte('priority_score', Number(f.scoreMin))
          if (f.scoreMax !== '') ucQ = ucQ.lte('priority_score', Number(f.scoreMax))
          if (f.tagFilter) {
            try {
              ucQ = ucQ.contains('tags', [f.tagFilter])
            } catch {
              /* legacy parity — ignore */
            }
          }
          if (f.phoneStatus) ucQ = ucQ.eq('phone_status', f.phoneStatus)
          if (f.emailStatus) ucQ = ucQ.eq('email_status', f.emailStatus)
          if (customContains) ucQ = ucQ.contains('custom_fields', customContains)

          const { data: ucRows, error: ucErr } = await ucQ
          if (cancelled) return
          if (ucErr) throw ucErr
          ucIncludeIds = (ucRows ?? []).map((r) => (r as { contact_id: string }).contact_id)
          if (!ucIncludeIds.length) {
            setRows([])
            setUcMap({})
            setTotal(0)
            setStatus('success')
            return
          }
        }

        // ── Stage 2: last-contacted exclusion ────────────────────────
        let lcExcludeIds: string[] | null = null
        if (f.lastContacted) {
          let exQ = supabase
            .from('user_contacts')
            .select('contact_id')
            .eq('user_id', userId)
            .not('last_contacted', 'is', null)
          if (f.lastContacted !== 'never') {
            const cutoff = new Date(Date.now() - Number(f.lastContacted) * 86400000).toISOString()
            exQ = exQ.gte('last_contacted', cutoff)
          }
          const { data: exRows } = await exQ
          if (cancelled) return
          lcExcludeIds = (exRows ?? []).map((r) => (r as { contact_id: string }).contact_id)
        }

        // ── Stage 3: contacts query ──────────────────────────────────
        let q = supabase
          .from('contacts')
          .select(
            'id,full_name,first_name,last_name,company_name,company_domain,job_title,primary_email,primary_phone,linkedin_url,city,state,country,industry',
            { count: 'exact' },
          )
          .range(fromIdx, toIdx)
          .order('full_name', { ascending: true })

        if (search) {
          q = q.or(
            `full_name.ilike.%${search}%,company_name.ilike.%${search}%,job_title.ilike.%${search}%,primary_email.ilike.%${search}%,primary_phone.ilike.%${search}%`,
          )
        }
        if (f.stage === '__nodomain') {
          q = q.is('company_domain', null).not('primary_email', 'is', null)
        } else if (f.stage === '__noemail') {
          q = q.is('primary_email', null).not('linkedin_url', 'is', null)
        } else if (ucIncludeIds !== null) {
          q = q.in('id', ucIncludeIds)
        }
        if (f.country) q = q.ilike('country', `%${f.country}%`)
        if (f.location) {
          q = q.or(
            `city.ilike.%${f.location}%,state.ilike.%${f.location}%,country.ilike.%${f.location}%`,
          )
        }
        if (f.phonePrefix) q = q.ilike('primary_phone', `${f.phonePrefix}%`)
        if (lcExcludeIds !== null && lcExcludeIds.length) {
          q = q.not('id', 'in', `(${lcExcludeIds.join(',')})`)
        }

        const { data, count, error: qErr } = await q
        if (cancelled) return
        if (qErr) throw qErr

        const contactRows = (data ?? []) as ContactRow[]

        // ── Stage 4: hydrate user_contacts overlay for the visible page ─
        let overlay: Record<string, UserContactOverlay> = {}
        if (contactRows.length) {
          const ids = contactRows.map((r) => r.id)
          const { data: ucPage } = await supabase
            .from('user_contacts')
            .select(
              'contact_id,lifecycle_stage,priority_score,last_contacted,call_notes,tags,phone_status,email_status',
            )
            .eq('user_id', userId)
            .in('contact_id', ids)
          if (cancelled) return
          overlay = (ucPage ?? []).reduce<Record<string, UserContactOverlay>>((acc, uc) => {
            const cid = (uc as { contact_id: string }).contact_id
            acc[cid] = uc as UserContactOverlay
            return acc
          }, {})
        }

        setRows(contactRows)
        setUcMap(overlay)
        setTotal(count ?? 0)
        setStatus('success')
      } catch (e) {
        if (cancelled) return
        setStatus('error')
        setError(e instanceof Error ? e.message : String(e))
        setRows([])
        setUcMap({})
        setTotal(0)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [filters, page, refreshKey, rotCutoff])

  return { rows, ucMap, total, status, error, rotDays, rotCutoff, refetch }
}
